import {
  useCallback,
  useMemo,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import { useQuery } from "react-query";
import { HtMedia } from "@hightouch/htui";
import { useUser } from "src/contexts/user-context";
import { captureException } from "src/lib/monitoring";
import { boundedResizeDimensions } from "src/pages/ad-studio/components/resize-dimensions";
import { useRegionConfig } from "src/utils/use-region-config";

export type ResizedImageFormat = "webp" | "jpg" | "png";

/**
 * How long a resolved presigned URL is considered fresh. Kept under the 1-hour
 * expiry the backend signs URLs with (see `core/server/routes/agents.ts`) so we
 * never serve a stale (expired) URL. Sharing the cached result across component
 * instances means a larger preview (e.g. a hover popover) can reuse the exact
 * URL a thumbnail already resolved and the browser already downloaded, so it
 * appears instantly with no loading flash.
 */
const PRESIGNED_URL_STALE_TIME_MS = 1000 * 60 * 30;

/**
 * Extracts a numeric pixel value from a CSS dimension.
 * Returns undefined otherwise if the value is not a number or a "{\d+}px" string.
 */
export function parsePxValue(value: unknown): number | undefined {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    const match = /^(\d+(?:\.\d+)?)px$/.exec(value);
    if (match?.[1]) {
      return Number(match[1]);
    }
  }
  return undefined;
}

/**
 * Constructs the authenticated API URL for an ad brief asset.
 * Handles both raw S3 storage keys and legacy GCS URLs.
 * Returns undefined for URLs that don't need transformation (data URLs, etc.).
 */
export function buildAdBriefAssetImageUrl(
  assetUrl: string | null | undefined,
  workspaceId: number | undefined,
  apiEndpoint: string,
  options:
    | {
        width: number | undefined;
        height: number | undefined;
        format: ResizedImageFormat | undefined;
      }
    | undefined,
): string | undefined {
  if (!assetUrl || !workspaceId) {
    return;
  }

  let s3Key: string | undefined;

  // Check if it's already a raw S3 storage key (e.g., "1/ad-artifacts/generated-images/...").
  // Content-library assets live under "<workspaceId>/images/<imageId>" (see
  // buildContentImageUrl); style references can point at one of those.
  if (
    (assetUrl.includes("/ad-artifacts/") ||
      assetUrl.includes("/ad-assets/") ||
      assetUrl.includes("/ad-product-catalog/") ||
      assetUrl.includes("/images/")) &&
    !assetUrl.startsWith("http") &&
    !assetUrl.startsWith("data:")
  ) {
    s3Key = assetUrl;
  } else {
    // Try to extract from a GCS URL (legacy path)
    try {
      const url = new URL(assetUrl);
      const matches = /(generated-images\/.*)$/.exec(url.pathname);
      const extractedPath = matches?.[1];
      if (extractedPath) {
        s3Key = `${workspaceId}/ad-artifacts/${extractedPath}`;
      }
    } catch {
      // Non-URL strings — fall through
    }
  }

  if (!s3Key) {
    return;
  }

  const params = new URLSearchParams({
    key: s3Key,
    redirect: "false",
  });

  if (options?.width) {
    params.set("width", options.width.toString());
  }
  if (options?.height) {
    params.set("height", options.height.toString());
  }
  if (options?.format) {
    params.set("format", options.format);
  }

  return `${apiEndpoint}/agents/files?${params.toString()}`;
}

/**
 * The cached lookup of a files-proxy URL to its presigned URL, keyed by the
 * proxy URL so every `AdStudioImage` (and the style editor's storage-key image
 * resolver) pointing at the same asset shares one fetch and one expiry.
 */
export function assetUrlQuery(apiUrl: string): {
  queryKey: readonly ["ad-studio-asset-url", string];
  queryFn: () => Promise<string>;
  staleTime: number;
} {
  return {
    queryKey: ["ad-studio-asset-url", apiUrl],
    queryFn: async () => {
      const response = await fetch(apiUrl, { credentials: "include" });
      if (!response.ok) {
        throw new Error(
          `Failed to resolve asset URL (status ${response.status})`,
        );
      }
      const body: { location: string } = await response.json();
      return body.location;
    },
    staleTime: PRESIGNED_URL_STALE_TIME_MS,
  };
}

/**
 * Resolves the presigned URL for an asset with credentials.
 * The API endpoint validates authentication and returns a presigned URL.
 * For URLs that don't need transformation (data URLs, etc.), returns the
 * original src unchanged.
 *
 * The resolution is cached (via React Query) keyed by the request URL, so every
 * `AdStudioImage` that points at the same asset and resize parameters shares a
 * single fetch and the same resolved URL. This lets a hover preview reuse the
 * URL a thumbnail already resolved instead of re-fetching from scratch.
 */
function useAuthenticatedAssetUrl(
  assetUrl: string | null | undefined,
  workspaceId: number | undefined,
  options:
    | {
        width: number | undefined;
        height: number | undefined;
        format: ResizedImageFormat | undefined;
      }
    | undefined,
): string | null | undefined {
  const { apiEndpoint } = useRegionConfig();

  const apiUrl = useMemo(
    () =>
      buildAdBriefAssetImageUrl(assetUrl, workspaceId, apiEndpoint, {
        width: options?.width,
        height: options?.height,
        format: options?.format,
      }),
    [
      assetUrl,
      workspaceId,
      apiEndpoint,
      options?.width,
      options?.height,
      options?.format,
    ],
  );

  const query = apiUrl ? assetUrlQuery(apiUrl) : null;
  const { data, isError } = useQuery(
    query?.queryKey ?? ["ad-studio-asset-url", undefined],
    query?.queryFn ?? (() => Promise.resolve("")),
    {
      enabled: query !== null,
      staleTime: PRESIGNED_URL_STALE_TIME_MS,
      onError: (err) => captureException(err),
    },
  );

  // Not an asset that needs transformation — pass through the original src.
  if (!apiUrl) {
    return assetUrl ?? null;
  }

  // On failure, fall back to the original src so `AdStudioImage` can attempt to
  // render (and run its own onError handling) rather than showing nothing.
  if (isError) {
    return assetUrl ?? null;
  }

  return data;
}

type AdStudioImageProps = ComponentPropsWithoutRef<typeof HtMedia> & {
  format?: ResizedImageFormat;

  /**
   * naturalWidth requests that the server resize the image to this many pixels wide.
   * Cf. https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/naturalWidth.
   **/
  naturalWidth?: number;

  /**
   * naturalHeight requests that the server resize the image to this many pixels high.
   * Cf. https://developer.mozilla.org/en-US/docs/Web/API/HTMLImageElement/naturalHeight.
   **/
  naturalHeight?: number;
};

/**
 * Renders an ad studio image with authenticated URL resolution.
 * Handles both raw S3 keys and legacy GCS URLs, resolving them to
 * presigned URLs via the /agents/files endpoint.
 */
export const AdStudioImage = ({
  src,
  onError,
  format = "webp",
  naturalWidth,
  naturalHeight,
  ...props
}: AdStudioImageProps) => {
  const { workspace } = useUser();

  const parsedWidth =
    naturalWidth ?? parsePxValue(props.width) ?? parsePxValue(props.maxWidth);

  const parsedHeight =
    naturalHeight ??
    parsePxValue(props.height) ??
    parsePxValue(props.maxHeight);

  const { width: resizeWidth, height: resizeHeight } = boundedResizeDimensions(
    parsedWidth,
    parsedHeight,
  );

  const resolvedSrc = useAuthenticatedAssetUrl(src, workspace?.id, {
    width: resizeWidth,
    height: resizeHeight,
    format,
  });

  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const handleError = useCallback<NonNullable<typeof onError>>(
    (e) => {
      if (failedSrc !== resolvedSrc && resolvedSrc !== src && src) {
        setFailedSrc(resolvedSrc ?? null);
      } else {
        onError?.(e);
      }
    },
    [failedSrc, resolvedSrc, src, onError],
  );

  if (!resolvedSrc) {
    return null;
  }

  const effectiveSrc = failedSrc === resolvedSrc && src ? src : resolvedSrc;

  return <HtMedia src={effectiveSrc} onError={handleError} {...props} />;
};
